import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getApprovedPaymentAccountsByType } from '@portal/utils/paymentAccount';
import {
  AuthActions,
  PaymentAccountActions,
  PixActions,
} from '@portal/actions';

import useReduxState from '@portal/hooks/useReduxState';
import * as StorageService from '~/services/storage';
import PanelNavigationStack from '@portal/containers/Panel/PanelNavigationStack';
import Logo from '@portal/assets/svg/logos/login_logo.svg?react';
import { LOGO_LABEL } from '@portal/config/env';
import Loading from '@portal/components/Loading/Loading';
import { translate } from '@portal/services/i18n';
import { Row, Col, Button } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useTheme } from '@material-ui/core';
import PaymentAccountSelector from '@portal/components/PaymentAccountSelector/PaymentAccountSelector';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';
import PixPortabilityModal from '@portal/components/PixPortabilityModal';

const PaymentAccountHandler: React.FC = () => {
  const dispatch = useDispatch();
  const { palette } = useTheme();
  const [requestPaymentAcc, setRequestPaymentAcc] = useState(true);
  const [portabilityModalVisible, setPortabilityModalVisible] = useState(false);
  const pixSetupFetched = useRef(false);

  const {
    user: { me: loggedUser, pendingPixKeyPortabilities },
    paymentAccount: { selectedPaymentAccount },
    loading: { amount: isLoading },
    theme: { currentTheme },
  } = useReduxState();

  const userId = loggedUser?.id ?? '';

  const isPortabilityRecentlyActed = useCallback(
    (key: string) => {
      if (!userId) return false;
      try {
        const data = JSON.parse(
          localStorage.getItem(
            StorageService.StorageItems.PIX_PORTABILITY_CONFIRMED
          ) ?? '{}'
        );
        const entry = data[`${userId}_${key}`];
        if (!entry) return false;
        if (Date.now() - entry > StorageService.PIX_PORTABILITY_CACHE_TTL) {
          delete data[`${userId}_${key}`];
          localStorage.setItem(
            StorageService.StorageItems.PIX_PORTABILITY_CONFIRMED,
            JSON.stringify(data)
          );
          return false;
        }
        return true;
      } catch {
        return false;
      }
    },
    [userId]
  );

  const markPortabilityActed = useCallback(
    (key: string) => {
      if (!userId) return;
      try {
        const data = JSON.parse(
          localStorage.getItem(
            StorageService.StorageItems.PIX_PORTABILITY_CONFIRMED
          ) ?? '{}'
        );
        data[`${userId}_${key}`] = Date.now();
        localStorage.setItem(
          StorageService.StorageItems.PIX_PORTABILITY_CONFIRMED,
          JSON.stringify(data)
        );
      } catch {
        /* noop */
      }
    },
    [userId]
  );

  useEffect(() => {
    if (loggedUser && loggedUser.account.paymentAccounts) {
      if (!selectedPaymentAccount) {
        const tempPayAccountsItems = getApprovedPaymentAccountsByType(
          loggedUser,
          PaymentAccountType.ACCOUNT
        );

        if (!tempPayAccountsItems) return;
        const localPaymentAccountId =
          StorageService.getSessionItem('payment-account-id');
        const currentSelected = tempPayAccountsItems.find(
          (item) => item.id === localPaymentAccountId
        );

        if (currentSelected) {
          dispatch(PaymentAccountActions.changePaymentAccount(currentSelected));
          setRequestPaymentAcc(false);
        } else if (tempPayAccountsItems.length === 1) {
          dispatch(
            PaymentAccountActions.changePaymentAccount(tempPayAccountsItems[0])
          );
          setRequestPaymentAcc(false);
        }
      } else {
        setRequestPaymentAcc(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, loggedUser.account.paymentAccounts, selectedPaymentAccount]);

  useEffect(() => {
    if (!requestPaymentAcc && !pixSetupFetched.current) {
      pixSetupFetched.current = true;
      dispatch(PixActions.getPixSetup());
    }
  }, [dispatch, requestPaymentAcc]);

  useEffect(() => {
    if (pendingPixKeyPortabilities && pendingPixKeyPortabilities.length > 0) {
      const firstKey = pendingPixKeyPortabilities[0].key;
      if (!isPortabilityRecentlyActed(firstKey)) {
        setPortabilityModalVisible(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPixKeyPortabilities, isPortabilityRecentlyActed]);

  const currentPortability =
    pendingPixKeyPortabilities && pendingPixKeyPortabilities.length > 0
      ? pendingPixKeyPortabilities[0]
      : null;

  const handleKeepKey = useCallback(async () => {
    if (!currentPortability) return;
    setPortabilityModalVisible(false);
    await dispatch(PixActions.deletePortability(currentPortability.key));
    markPortabilityActed(currentPortability.key);
  }, [dispatch, currentPortability, markPortabilityActed]);

  const handleUseInOtherAccount = useCallback(async () => {
    if (!currentPortability) return;
    setPortabilityModalVisible(false);
    await dispatch(
      PixActions.portabilityConfirmSetup(currentPortability.key, true)
    );
    markPortabilityActed(currentPortability.key);
  }, [dispatch, currentPortability, markPortabilityActed]);

  return requestPaymentAcc ? (
    <div className="payacc">
      {isLoading > 0 && <Loading />}
      <Helmet>
        <title>{translate('PAGES.AUTH.LOGIN.PAGE_TITLE')}</title>
      </Helmet>
      <Row className="payacc__container">
        <Col
          span={14}
          className="payacc__container-left"
          style={{ background: currentTheme?.background }}
        >
          <div className="payacc__text">
            <h2>
              {translate('PAGES.AUTH.LOGIN.SLOGAN.ONE')}
              <br /> {translate('PAGES.AUTH.LOGIN.SLOGAN.TWO')}
            </h2>
          </div>
        </Col>
        <Col span={10} className="payacc__container__form">
          <div className="payacc__container__form__inner">
            <Row justify="center">
              <Col span={24}>
                <Col>
                  <div className="payacc__container__form__inner__logo">
                    <Logo
                      className="payacc__container__form__inner__logo__svg"
                      fill={palette.primary.main}
                      height={70}
                    />
                  </div>
                  {LOGO_LABEL ? (
                    <a
                      className="payacc__container__form__inner__message__label"
                      href="https://mblabs.com.br/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {translate('SHARED.POWERED_BY')}
                    </a>
                  ) : null}
                </Col>
                <Col className="payacc__container__form__inner__form">
                  <p className="payacc__container__form__inner__text">
                    {translate('PAGES.AUTH.ACCOUNT_CHECK.TITLE')}
                  </p>
                  <Row
                    justify="center"
                    className="payacc__container__form__inner__form__user"
                  >
                    <Col span={24}>
                      <PaymentAccountSelector />
                    </Col>
                  </Row>
                </Col>
                <div className="payacc__container__form__inner__links">
                  <Button
                    className="payacc__container__form__inner__link"
                    type="link"
                    onClick={() => {
                      dispatch(AuthActions.logout());
                    }}
                  >
                    <p className="payacc__container__form__inner__link-text">
                      {translate('PAGES.AUTH.ACCOUNT_CHECK.LOGOUT')}
                    </p>
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  ) : (
    <>
      <PanelNavigationStack />
      <PixPortabilityModal
        visible={portabilityModalVisible}
        setVisible={setPortabilityModalVisible}
        pendingPortability={currentPortability}
        onKeepKey={handleKeepKey}
        onUseInOtherAccount={handleUseInOtherAccount}
      />
    </>
  );
};

export default PaymentAccountHandler;
